export interface SwatchedPartTextureMaskPair {
  textureDatasetKey: string;
  maskDatasetKey: string;
}
/**
 * A set is used to represent each potential swatchId of a given part
 * (someone could use the pig's helmet with "lunar-white", and someone else could use the pig's helmet with "blood-red", etc.)
 * However many different swatches need to be made, they only share a single mask.
 */
export interface SwatchedPartTextureDataCollection extends SwatchedPartTextureMaskPair {
  swatchIds: Set<string>;
}
export interface SwatchedPartTextureGeneration extends SwatchedPartTextureMaskPair {
  swatchIds: string[];
}

// Pipeline types - pure type definitions for swatch/pipeline data structures

/**
 * Enum for pipeline types used in texture manipulation.
 */
export enum PipelineType {
  textureDefault = 'textureDefault',
  tint = 'tint',
  colourMatrix = 'colourMatrix'
}

/**
 * Type guard to check if a string is a valid PipelineType.
 */
export function isPipelineType(str: string): str is PipelineType {
  return str in PipelineType;
}

/**
 * Union type for pipeline types (derived from PipelineType enum).
 */
export type UPipelineType = keyof typeof PipelineType;

/** RGB colour values (e.g. 0–1 for tint). Used by pipeline tint params and elsewhere. */
export interface IRgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Entry in a pipeline series with any params (used for config data before processing).
 */
export interface PipelineSeriesEntry_AnyParams {
  type: UPipelineType;
  params: any;
}

/**
 * Array of pipeline series entries, used during build/config phase.
 */
export type PipelineSeriesOnBuild = PipelineSeriesEntry_AnyParams[];

/**
 * Pipeline series organized by series name.
 */
export type PipelineSeriesByName = { [seriesName: string]: PipelineSeriesOnBuild };

/**
 * Pipeline series organized by region colour name.
 */
export type PipelineSeriesByRegionColour = { [colourName: string]: PipelineSeriesOnBuild };

/**
 * A collection of swatches, each containing pipeline series by region colour.
 * This is the main type used for swatch configuration data.
 */
export type SwatchDataByName = { [swatchName: string]: PipelineSeriesByRegionColour };
